import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Flex, Row, Col, Typography, Spin } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { PAIC_TAB_KEYS } from "constants/peoplePlusAI.constants";
import GroupPageHeader from "Pages/SkillsBridgerV2/HrSkillsBridger/shared/GroupPageHeader";
import SkillsChart from "./components/SkillsChart";
import SbiCard from "./components/SbiCard";
import EmployeeProfileCard from "./components/EmployeeProfileCard";
import {
  SkillsMapContainerStyled,
  ContentAreaStyled,
  ComingSoonStyled,
} from "./SbSkillsMapV2.styles";
import { TEXT, ROUTE } from "./SbSkillsMapV2.constants";
import {
  PAGE_TEXT,
  SKILLS_BRIDGER_DROPDOWN_ITEMS,
  COMING_SOON_TABS,
} from "./GroupSkillsMapV2.constants";
import { transformSkillsForChart } from "./EmployeeSkillsMapV2.constants";
import { fetchGroupJdSkillAnalysis } from "Api/apiService";
import { buildGroupHeader } from "Pages/SkillsBridgerV2/HrSkillsBridger/shared/GroupPageHeader/groupPageHeader.utils";
import useGroupProfileSummary from "./hooks/useGroupProfileSummary";

import useAnalytics from "Hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "Utils/analytics";
const { Text } = Typography;

const ANALYTICS_FROM = "skills_bridger_group_skills_map";
const ANALYTICS_EVENT = ANALYTICS_EVENTS.SKILLS_BRIDGER.SKILLS_MAP_VIEWED;

const GroupSkillsMapV2 = () => {
  const track = useAnalytics(ANALYTICS_FROM);
  // This screen has no Mixpanel coverage of its own; the automatic
  // pageview only records the URL, not that the module was reached.
  useEffect(() => {
    track(ANALYTICS_EVENT);
  }, [track]);
  const navigate   = useNavigate();
  const { state }  = useLocation();

  const selectedEmployees = state?.selectedEmployees ?? [];
  const { title: headerTitle, subtitle: headerSubtitle } = useMemo(
    () => buildGroupHeader(selectedEmployees),
    [selectedEmployees]
  );

  const [groupSkillAnalysis, setGroupSkillAnalysis] = useState(state?.groupSkillAnalysis ?? null);
  const [activeTab,          setActiveTab]          = useState(PAIC_TAB_KEYS.SKILLS_BRIDGER);
  const [chartSkills,        setChartSkills]        = useState([]);
  const [groupSbi,           setGroupSbi]           = useState("--");
  const [analysisLoading,    setAnalysisLoading]    = useState(false);

  const { summary: groupSummary, isLoading: summaryLoading } = useGroupProfileSummary(
    selectedEmployees,
    state?.groupSummary ?? null,
  );

  // ── Fetch skill analysis (fallback when not in router state) ──────────────
  // Normally pre-fetched in SkillsBridgerV2 before navigation; this fallback
  // only runs when the page is reached directly (e.g. bookmarked URL).

  useEffect(() => {
    if (groupSkillAnalysis || !selectedEmployees.length) return;
    setAnalysisLoading(true);
    fetchGroupJdSkillAnalysis(selectedEmployees)
      .then(setGroupSkillAnalysis)
      .catch((err) => console.error("GroupSkillsMapV2: skill analysis fetch failed —", err))
      .finally(() => setAnalysisLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Process analysis data ──────────────────────────────────────────────────

  useEffect(() => {
    if (!groupSkillAnalysis) return;

    const analysis = groupSkillAnalysis?.personProfileAnalysis ?? {};
    const skills   = analysis.skills ?? [];
    const tasks    = analysis.tasks  ?? [];

    setChartSkills(transformSkillsForChart(skills, tasks));

    const rawSbi = groupSkillAnalysis?.current_vs_required_SBI_score;
    setGroupSbi(rawSbi != null ? `${Math.round(rawSbi)}%` : "--");
  }, [groupSkillAnalysis]);

  // ── Navigation ─────────────────────────────────────────────────────────────

  const handleBack = useCallback(
    () => navigate(ROUTE.SKILLS_BRIDGER),
    [navigate]
  );

  // Forwards all fetched data to proficiency page — no re-fetch there.
  const handleNavigateToProficiency = useCallback(
    () => navigate(ROUTE.GROUP_SKILLS_PROFICIENCY, {
      state: { selectedEmployees, groupSkillAnalysis, groupSummary },
    }),
    [navigate, selectedEmployees, groupSkillAnalysis, groupSummary]
  );

  // ── Dropdown items ─────────────────────────────────────────────────────────

  const rightDropdownItems = useMemo(
    () => [
      {
        key:     "skills-proficiency",
        label:   TEXT.DROPDOWN_SKILLS_PROFICIENCY,
        onClick: handleNavigateToProficiency,
      },
    ],
    [handleNavigateToProficiency]
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <SkillsMapContainerStyled vertical>

      <GroupPageHeader
        activeTab={activeTab}
        onBack={handleBack}
        onTabChange={setActiveTab}
        skillsBridgerDropdownItems={SKILLS_BRIDGER_DROPDOWN_ITEMS}
        rightDropdownLabel={TEXT.DROPDOWN_SKILLS_MAP}
        rightDropdownItems={rightDropdownItems}
        pageTitle={headerTitle}
        pageSubtitle={headerSubtitle}
      />

      <ContentAreaStyled flex="1">

        {activeTab === PAIC_TAB_KEYS.SKILLS_BRIDGER && (
          <Spin spinning={analysisLoading}>
            <Row gutter={[24, 24]} align="top">
              <Col xs={24} lg={16}>
                <SkillsChart
                  skills={chartSkills}
                  title={PAGE_TEXT.CHART_TITLE}
                  subtitle={PAGE_TEXT.CHART_SUBTITLE}
                />
              </Col>
              <Col xs={24} lg={8}>
                <Flex vertical gap={16}>
                  <SbiCard label={PAGE_TEXT.SBI_LABEL} value={groupSbi} />
                  <EmployeeProfileCard
                    label={PAGE_TEXT.PROFILE_LABEL}
                    insights={groupSummary}
                    insightsLoading={summaryLoading}
                    profileText={!groupSummary && !summaryLoading ? TEXT.NO_SUMMARY_DATA : ""}
                    loading={summaryLoading}
                    isEmpty={!groupSummary && !summaryLoading}
                  />
                </Flex>
              </Col>
            </Row>
          </Spin>
        )}

        {COMING_SOON_TABS.has(activeTab) && (
          <ComingSoonStyled align="center" justify="center">
            <Text type="secondary">{TEXT.COMING_SOON}</Text>
          </ComingSoonStyled>
        )}

      </ContentAreaStyled>

    </SkillsMapContainerStyled>
  );
};

export default GroupSkillsMapV2;
